import React, { useState, useEffect } from 'react';
import { X, UserPlus, Edit2, Loader2 } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import styles from './AddStaffModal.module.css';

const AddStaffModal = ({ isOpen, onClose, onStaffAdded, initialData }) => {
  const { activeStore } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', phone: '', email: '', role: 'STAFF', status: 'Active'
  });

  const isEditing = !!initialData;

  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        role: initialData.designation || 'STAFF', // Mapped from backend designation
        status: initialData.status || 'Active'
      });
    } else {
      setFormData({ firstName: '', lastName: '', phone: '', email: '', role: 'STAFF', status: 'Active' });
    }
    setErrorMsg('');
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');

    try {
      const token = localStorage.getItem('smiify_token');
      const storeId = activeStore?._id || activeStore?.id;

      if (!isEditing && (!storeId || storeId === 'global_hq')) {
        setErrorMsg('Please select a specific branch from the top dropdown to add staff.');
        setIsLoading(false);
        return;
      }

      // 🚀 THE FIX: Map 'role' to 'designation' for backend validation
      // and explicitly pass 'assignedStores' as an array for strict check.
      const payload = { 
          ...formData, 
          designation: formData.role, // Backend requires this
          storeId: storeId,
          assignedStores: [storeId]   // Ensures store validation passes
      };

      const url = isEditing 
        ? `http://localhost:5001/api/v1/smiify/sm/staff/${initialData.id}` 
        : `http://localhost:5001/api/v1/smiify/sm/staff`;

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (onStaffAdded) onStaffAdded(data.data || data.staff, isEditing); 
        onClose();
      } else {
        setErrorMsg(data.message || 'Failed to process request');
      }
    } catch (error) {
      setErrorMsg('Network error. Please check backend connection.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        
        <div className={styles.modalHeader}>
          <div className={styles.headerTitleWrap}>
            <div className={styles.headerIcon}>
              {isEditing ? <Edit2 size={20} color="var(--accent-color)" /> : <UserPlus size={20} color="var(--accent-color)" />}
            </div>
            <div>
              <h3 className={styles.modalTitle}>{isEditing ? 'Edit Staff Profile' : 'Add New Staff'}</h3>
              <p className={styles.modalSubtitle}>Branch: <strong>{activeStore?.storeName || 'Active Store'}</strong></p>
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} disabled={isLoading}><X size={18} /></button>
        </div>

        {errorMsg && <div style={{ color: '#dc2626', backgroundColor: '#fef2f2', padding: '10px', borderRadius: '6px', fontSize: '13px', marginBottom: '15px', border: '1px solid #fecaca' }}>{errorMsg}</div>}

        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>First Name <span className={styles.required}>*</span></label>
              <input type="text" name="firstName" placeholder="e.g. Javed" value={formData.firstName} onChange={handleChange} required />
            </div>

            <div className={styles.formGroup}>
              <label>Last Name</label>
              <input type="text" name="lastName" placeholder="e.g. Habib" value={formData.lastName} onChange={handleChange} />
            </div>

            <div className={styles.formGroup}>
              <label>Phone Number</label>
              <input type="text" name="phone" placeholder="+91" value={formData.phone} onChange={handleChange} />
            </div>

            <div className={styles.formGroup}>
              <label>Email Address</label>
              <input type="email" name="email" placeholder="javed@example.com" value={formData.email} onChange={handleChange} />
            </div>

            <div className={styles.formGroup}>
              <label>Role / Designation <span className={styles.required}>*</span></label>
              <select name="role" value={formData.role} onChange={handleChange} required>
                <option value="ADMIN">Admin (Full Access)</option>
                <option value="MANAGER">Manager (Branch Operations)</option>
                <option value="RECEPTIONIST">Receptionist (Billing & Bookings)</option>
                <option value="STAFF">Staff (Basic Services)</option>
              </select>
            </div>

            {isEditing && (
              <div className={styles.formGroup}>
                <label>Status <span className={styles.required}>*</span></label>
                <select name="status" value={formData.status} onChange={handleChange} required>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          <div className={styles.modalActions}>
            <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={isLoading}>Cancel</button>
            <button type="submit" className={styles.primaryBtn} disabled={isLoading}>
              {isLoading ? <><Loader2 size={16} className="spinner" /> Saving...</> : isEditing ? 'Save Changes' : 'Add Staff'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default AddStaffModal;